import React from 'react';
import { Box } from '@mui/material';
import { InformationChip } from '@europrocurement/flexy-components';
import { getPriorityLabel } from '../../constant';
import useFadeOverflow, { FADE_MASK_SX } from '../../hook/useFadeOverflow';

type TaskPriorityChipProps = {
    taskPriority?: number;
    endAdornment?: React.ReactNode;
    compact?: boolean;
};

const TaskPriorityChip: React.FunctionComponent<TaskPriorityChipProps> = function ({
    taskPriority,
    endAdornment,
    compact = false,
}) {
    const color = (() => {
        switch (taskPriority) {
            case 1:
                return 'warning';
            case 2:
                return 'error';
            default:
                return 'primary';
        }
    })();

    const { ref: textRef, isOverflowing } = useFadeOverflow<HTMLSpanElement>([
        taskPriority,
        compact,
    ]);

    const compactSx =
        compact && endAdornment
            ? {
                  maxWidth: '100px',
                  '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      width: '100%',
                  },
              }
            : {};

    let label: React.ReactNode;
    if (compact && endAdornment) {
        label = (
            <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'center', gap: '3px', width: '100%' }}
            >
                <Box
                    component="span"
                    ref={textRef}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        ...(isOverflowing && FADE_MASK_SX),
                    }}
                >
                    {getPriorityLabel(taskPriority)}
                </Box>
                <Box
                    component="span"
                    sx={{ flexShrink: 0, lineHeight: 0 }}
                >
                    {endAdornment}
                </Box>
            </Box>
        );
    } else if (endAdornment) {
        label = (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {getPriorityLabel(taskPriority)}
                {endAdornment}
            </Box>
        );
    } else {
        label = getPriorityLabel(taskPriority);
    }

    return (
        <Box data-testid="task-priority-chip">
            <InformationChip
                label={label}
                color={color}
                sx={{ pointerEvents: 'none', ...compactSx }}
            />
        </Box>
    );
};

export default TaskPriorityChip;
